/**
 * Chat UI Component
 * Handles message rendering, user interactions, and TTS integration
 */

class ChatUI {
    constructor(container) {
        this.container = container;
        this.messagesContainer = document.getElementById('chat-messages');
        this.inputField = document.getElementById('chat-input');
        this.sendButton = document.getElementById('btn-send');
        this.contextBadge = document.getElementById('context-badge');

        this.messages = [];
        this.isGenerating = false;
        this.currentStreamingMessage = null;

        this.onSendMessage = null;
        this.onSpeakMessage = null; // TTS callback

        this.setupEventListeners();
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Send button click
        this.sendButton.addEventListener('click', () => this.handleSend());

        // Enter key handling
        this.inputField.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleSend();
            }
        });

        // Auto-resize textarea
        this.inputField.addEventListener('input', () => {
            this.inputField.style.height = 'auto';
            this.inputField.style.height = Math.min(this.inputField.scrollHeight, 150) + 'px';
        });
    }

    /**
     * Handle send message
     */
    handleSend() {
        const text = this.inputField.value.trim();
        if (!text || this.isGenerating) return;

        // Clear input
        this.inputField.value = '';
        this.inputField.style.height = 'auto';

        // Add user message
        this.addMessage('user', text);

        // Callback
        if (this.onSendMessage) {
            this.onSendMessage(text);
        }
    }

    /**
     * Create speak button HTML
     * @returns {string}
     */
    createSpeakButton() {
        return `
      <button class="btn-speak" title="Écouter">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
        </svg>
      </button>
    `;
    }

    /**
     * Add a message to the chat
     * @param {string} role - 'user' or 'assistant'
     * @param {string} content - Message content
     * @param {boolean} showSpeak - Whether to show speak button
     * @returns {HTMLElement}
     */
    addMessage(role, content, showSpeak = true) {
        const messageEl = document.createElement('div');
        messageEl.className = `message ${role}`;

        const avatarIcon = role === 'user'
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>';

        const speakButtonHtml = (showSpeak && role === 'assistant') ? this.createSpeakButton() : '';

        messageEl.innerHTML = `
      <div class="message-avatar">${avatarIcon}</div>
      <div class="message-content">
        <p>${this.escapeHtml(content)}</p>
        <div class="message-actions">
          ${speakButtonHtml}
        </div>
      </div>
    `;

        // Add TTS event listener
        if (showSpeak && role === 'assistant') {
            const speakBtn = messageEl.querySelector('.btn-speak');
            if (speakBtn) {
                speakBtn.addEventListener('click', () => {
                    if (this.onSpeakMessage) {
                        // Toggle speaking state
                        const isSpeaking = speakBtn.classList.contains('speaking');
                        if (isSpeaking) {
                            this.onSpeakMessage(null); // Stop
                            speakBtn.classList.remove('speaking');
                        } else {
                            // Stop all others
                            this.messagesContainer.querySelectorAll('.btn-speak.speaking').forEach(btn => {
                                btn.classList.remove('speaking');
                            });
                            speakBtn.classList.add('speaking');
                            this.onSpeakMessage(content, () => {
                                speakBtn.classList.remove('speaking');
                            });
                        }
                    }
                });
            }
        }

        this.messagesContainer.appendChild(messageEl);
        this.scrollToBottom();

        this.messages.push({ role, content });

        return messageEl;
    }

    /**
     * Start a streaming message
     * @returns {HTMLElement}
     */
    startStreamingMessage() {
        const messageEl = document.createElement('div');
        messageEl.className = 'message assistant';

        messageEl.innerHTML = `
      <div class="message-avatar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      </div>
      <div class="message-content">
        <div class="typing-indicator">
          <span></span><span></span><span></span>
        </div>
      </div>
    `;

        this.messagesContainer.appendChild(messageEl);
        this.scrollToBottom();

        this.currentStreamingMessage = messageEl;
        this.isGenerating = true;
        this.setInputEnabled(false);

        return messageEl;
    }

    /**
     * Update streaming message content
     * @param {string} content - Current full content
     */
    updateStreamingMessage(content) {
        if (!this.currentStreamingMessage) return;

        const contentEl = this.currentStreamingMessage.querySelector('.message-content');
        contentEl.innerHTML = `<p>${this.formatContent(content)}</p>`;
        this.scrollToBottom();
    }

    /**
     * Finish streaming message
     * @param {string} finalContent - Final message content
     */
    finishStreamingMessage(finalContent) {
        if (this.currentStreamingMessage) {
            const contentEl = this.currentStreamingMessage.querySelector('.message-content');
            contentEl.innerHTML = `
        <p>${this.formatContent(finalContent)}</p>
        <div class="message-actions">
          ${this.createSpeakButton()}
        </div>
      `;

            // Add TTS event listener
            const speakBtn = this.currentStreamingMessage.querySelector('.btn-speak');
            if (speakBtn) {
                speakBtn.addEventListener('click', () => {
                    if (this.onSpeakMessage) {
                        const isSpeaking = speakBtn.classList.contains('speaking');
                        if (isSpeaking) {
                            this.onSpeakMessage(null);
                            speakBtn.classList.remove('speaking');
                        } else {
                            this.messagesContainer.querySelectorAll('.btn-speak.speaking').forEach(btn => {
                                btn.classList.remove('speaking');
                            });
                            speakBtn.classList.add('speaking');
                            this.onSpeakMessage(finalContent, () => {
                                speakBtn.classList.remove('speaking');
                            });
                        }
                    }
                });
            }

            this.messages.push({ role: 'assistant', content: finalContent });
        }

        this.currentStreamingMessage = null;
        this.isGenerating = false;
        this.setInputEnabled(true);
        this.scrollToBottom();
    }

    /**
     * Add error message
     * @param {string} error - Error text
     */
    addErrorMessage(error) {
        if (this.currentStreamingMessage) {
            this.currentStreamingMessage.remove();
            this.currentStreamingMessage = null;
        }

        const messageEl = document.createElement('div');
        messageEl.className = 'message assistant';
        messageEl.innerHTML = `
      <div class="message-avatar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      </div>
      <div class="message-content" style="border-color: rgba(239, 68, 68, 0.3);">
        <p style="color: #FCA5A5;">Erreur: ${this.escapeHtml(error)}</p>
      </div>
    `;

        this.messagesContainer.appendChild(messageEl);
        this.scrollToBottom();

        this.isGenerating = false;
        this.setInputEnabled(true);
    }

    /**
     * Get messages for LLM context
     * @returns {Array}
     */
    getMessagesForContext() {
        return this.messages.filter(m => m.role === 'user' || m.role === 'assistant');
    }

    /**
     * Enable/disable input
     * @param {boolean} enabled
     */
    setInputEnabled(enabled) {
        this.inputField.disabled = !enabled;
        this.sendButton.disabled = !enabled;

        if (enabled) {
            this.inputField.focus();
        }
    }

    /**
     * Show/hide PDF context badge
     * @param {boolean} show
     */
    showContextBadge(show) {
        this.contextBadge.classList.toggle('hidden', !show);
    }

    /**
     * Scroll chat to bottom
     */
    scrollToBottom() {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }

    /**
     * Escape HTML entities
     * @param {string} text
     * @returns {string}
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Format content with basic markdown support
     * @param {string} content
     * @returns {string}
     */
    formatContent(content) {
        return this.escapeHtml(content)
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code>$1</code>')
            .replace(/\n/g, '<br>');
    }

    /**
     * Clear chat history (except welcome message)
     */
    clearHistory() {
        this.messages = [];
        const welcomeMessage = this.messagesContainer.firstElementChild;
        this.messagesContainer.innerHTML = '';
        if (welcomeMessage) {
            this.messagesContainer.appendChild(welcomeMessage);
        }
    }
}

export default ChatUI;
